/**
 * DRPX Courier — Route Configuration
 *
 * Zentrales Mapping zwischen URL-Pfaden und internen Kennungen.
 * Wird von App.jsx (Route-Definitionen), useNavigation (nav-Funktion),
 * AppShell (aktiver Menüpunkt) und Pages (navigate) genutzt.
 *
 * @module config/routes
 */

// ═══ Pfad-Konstanten ═══════════════════════════════════════════════════════════
// Jeder Pfad wird nur EINMAL definiert — kein Copy-Paste-Fehlerrisiko.

/** Zentrale Pfad-Definitionen für alle Routen (Single Source of Truth). */
export const PATHS = {
  // Public
  login: '/login',
  track: '/track/:token',
  recovery: '/recovery',
  einladung: '/einladung',

  // Firma
  dashboard: '/dashboard',
  auftraege: '/auftraege',
  adressbuch: '/adressbuch',
  abrechnung: '/abrechnung',
  favoriten: '/favoriten',
  team: '/team',

  // Fahrer – Eigenes Geschäft
  eigeneauftraege: '/eigene-auftraege',
  kunden: '/kunden',
  fahrtenbuch: '/fahrtenbuch',
  finanzen: '/finanzen',
  'rechnungen-eigen': '/rechnungen',
  tourenplaner: '/tourenplaner',

  // Marktplatz (Fahrer)
  marktplatz: '/marktplatz',
  meine: '/meine-angebote',
  meineauftraege: '/laufende-auftraege',
  zertifikate: '/zertifikate',
  auszahlung: '/auszahlung',

  // Gemeinsam
  abo: '/abo',
  profil: '/profil',
  bewertungen: '/bewertungen',
  roteListe: '/rote-liste',
  ueberuns: '/ueber-uns',

  // Admin
  admin: '/admin',
  'admin-health': '/admin/health',
  'admin-funnel': '/admin/funnel',
  'admin-blacklist': '/admin/blacklist',
  'admin-users': '/admin/users',
  'admin-auftraege': '/admin/auftraege',
  'admin-abos': '/admin/abos',
  'admin-reengagement': '/admin/reengagement',
  'admin-map': '/admin/map',
  'admin-usage': '/admin/usage',
  'admin-monitoring': '/admin/monitoring',
  'admin-bewertungen': '/admin/bewertungen',
  'admin-finanzen': '/admin/finanzen',
  'admin-settings': '/admin/settings',
  'admin-audit': '/admin/audit',
} as const satisfies Record<string, string>;

/** Union-Type aller bekannten Tab-IDs. */
export type TabId = keyof typeof PATHS;

// ═══ Reverse-Map: Pfad → Tab-ID ═══════════════════════════════════════════════
// Wird genutzt um aus der aktuellen URL den aktiven Nav-Tab abzuleiten.

const _pathToTab: Record<string, string> = {};
for (const tabId of Object.keys(PATHS) as TabId[]) {
  _pathToTab[PATHS[tabId]] = tabId;
}

/**
 * startTab — Start-Tab-ID nach Login je Rolle.
 * Fahrer landen direkt in der Auftragssuche (Marktplatz), Firmen auf dem
 * Dashboard, Admins im Adminbereich. Entscheidung 04.06.2026 (Variante A):
 * Fahrer IMMER → Marktplatz, auch ohne Abo (AboGate) oder Verifizierung
 * (Banner) — beides ist dort als Call-to-Action sichtbar.
 */
export function startTab(rolle?: string): TabId {
  if (rolle === 'admin') return 'admin';
  if (rolle === 'fahrer') return 'marktplatz';
  return 'dashboard';
}

/** startPath — Start-Route nach Login je Rolle. @see startTab */
export function startPath(rolle?: string): string {
  return PATHS[startTab(rolle)];
}

/**
 * tabToPath — Übersetzt eine Tab-ID in den URL-Pfad.
 * Fallback: '/dashboard' falls unbekannt.
 */
export function tabToPath(tabId: string): string {
  return (PATHS as Record<string, string>)[tabId] || '/dashboard';
}

/**
 * pathToTab — Übersetzt einen URL-Pfad in die Tab-ID.
 * Versteht auch /admin/users → 'admin-users' etc.
 * Fallback: '' (leerer String, wie bisher bei unbekanntem Tab).
 */
export function pathToTab(pathname: string): string {
  // Exakter Match
  if (_pathToTab[pathname]) return _pathToTab[pathname];

  // Admin-Subrouten: /admin/users → 'admin-users'
  if (pathname.startsWith('/admin/')) {
    const sub = pathname.replace('/admin/', '');
    const adminTab = 'admin-' + sub;
    if ((PATHS as Record<string, string>)[adminTab]) return adminTab;
  }

  // /track/:token → spezieller Fall
  if (pathname.startsWith('/track/')) return '__tracking__';

  // /recovery → spezieller Fall
  if (pathname === '/recovery') return '__recovery__';

  // /login → spezieller Fall
  if (pathname === '/login') return '__login__';

  return '';
}

/**
 * Erkennt den Passwort-Link aus der Mail: Pfad `/recovery` mit
 * `token_hash` und `type=recovery` in der Adresse.
 *
 * Mehrfache Schrägstriche am Anfang werden toleriert — endet die Site URL
 * in Supabase mit „/", entsteht aus `{{ .SiteURL }}/recovery` sonst
 * `//recovery`.
 *
 * @param loc zu prüfende Adresse (Standard: aktuelle Seite)
 * @returns true, wenn ein einzulösender Passwort-Link vorliegt
 */
export function istRecoveryLink(
  loc: Pick<Location, 'pathname' | 'search'> = window.location,
): boolean {
  const pfad = loc.pathname.replace(/^\/+/, '/').replace(/\/+$/, '');
  if (pfad !== PATHS.recovery) return false;
  const params = new URLSearchParams(loc.search);
  return !!params.get('token_hash') && params.get('type') === 'recovery';
}

/**
 * Erkennt den Link aus der Team-Einladung (DRPX-Pro, Migration 141): Pfad
 * `/einladung` mit `token_hash` und `type=invite` (Supabase-Vorlage
 * „Invite user"). Mehrfache Schrägstriche am Anfang wie bei
 * {@link istRecoveryLink}.
 *
 * @param loc zu prüfende Adresse (Standard: aktuelle Seite)
 * @returns true, wenn ein einzulösender Einladungs-Link vorliegt
 */
export function istEinladungLink(
  loc: Pick<Location, 'pathname' | 'search'> = window.location,
): boolean {
  const pfad = loc.pathname.replace(/^\/+/, '/').replace(/\/+$/, '');
  if (pfad !== PATHS.einladung) return false;
  const params = new URLSearchParams(loc.search);
  return !!params.get('token_hash') && params.get('type') === 'invite';
}

/**
 * getTrackingUrl — Erzeugt die öffentliche Tracking-URL.
 */
export function getTrackingUrl(token: string): string {
  return window.location.origin + '/track/' + encodeURIComponent(token);
}
